import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import api from '../../utils/api';
import { useTenant } from '../../context/TenantContext';
import { exportCsv, exportExcel, exportPdf } from '../../utils/export';

const Exports = () => {
  const { activeTenant, onboardingStatus } = useTenant();
  const [invoices, setInvoices] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadData = async () => {
    if (!activeTenant) return;
    try {
      setLoading(true);
      const [invRes, expRes] = await Promise.all([
        api.get(`/native/invoices/${activeTenant}`),
        api.get(`/native/expenses/${activeTenant}`),
      ]);
      setInvoices(invRes.data);
      setExpenses(expRes.data);
    } catch (error) {
      console.error('Error loading exports data:', error);
      toast.error('Error al cargar datos de exportacion');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [activeTenant]);

  const isReadyToOperate = onboardingStatus?.is_ready_to_operate === true;
  const hasSubmittedOnboarding =
    onboardingStatus?.onboarding_status && onboardingStatus.onboarding_status !== 'DRAFT';

  const invoiceRows = invoices.map((inv) => ({
    invoice_number: inv.invoice_number,
    date: inv.invoice_date,
    client: inv.client_name,
    total: inv.total,
    status: inv.status,
  }));

  const expenseRows = expenses.map((exp) => ({
    date: exp.expense_date,
    supplier: exp.supplier_name,
    category: exp.category,
    total: exp.total_amount,
    paid: exp.is_paid ? 'si' : 'no',
    status: exp.status,
  }));

  return (
    <div data-testid="client-exports-page" className="max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-4xl font-heading font-bold">Exportar datos</h1>
        <p className="text-muted-foreground">CSV, Excel y PDF para facturas y gastos</p>
      </div>

      {!hasSubmittedOnboarding && (
        <div className="mb-6 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700">
          Completa la incorporacion para activar Exportar datos.
        </div>
      )}

      {loading && <p className="text-muted-foreground mb-4">Cargando datos...</p>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-5 space-y-3">
            <div className="text-lg font-semibold">Facturas</div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={() => exportCsv(invoiceRows, 'facturas.csv')}
                disabled={!isReadyToOperate}
              >
                Exportar CSV
              </Button>
              <Button
                variant="outline"
                onClick={() => exportExcel(invoiceRows, 'facturas.xlsx')}
                disabled={!isReadyToOperate}
              >
                Exportar Excel
              </Button>
              <Button
                variant="outline"
                onClick={() => exportPdf(invoiceRows, 'facturas.pdf', 'Facturas')}
                disabled={!isReadyToOperate}
              >
                Exportar PDF
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5 space-y-3">
            <div className="text-lg font-semibold">Gastos</div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={() => exportCsv(expenseRows, 'gastos.csv')}
                disabled={!isReadyToOperate}
              >
                Exportar CSV
              </Button>
              <Button
                variant="outline"
                onClick={() => exportExcel(expenseRows, 'gastos.xlsx')}
                disabled={!isReadyToOperate}
              >
                Exportar Excel
              </Button>
              <Button
                variant="outline"
                onClick={() => exportPdf(expenseRows, 'gastos.pdf', 'Gastos')}
                disabled={!isReadyToOperate}
              >
                Exportar PDF
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Exports;
